import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { File01Icon, Upload01Icon, EyeIcon, Delete02Icon } from '@hugeicons/core-free-icons';
import { CaseService } from '../../../../core/services/case.service';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';

interface DocItem {
    name: string;
    path: string;
    label: string;
}

interface DocGroup {
    title: string;
    docs: DocItem[];
}

@Component({
    selector: 'app-case-document-center',
    standalone: true,
    imports: [CommonModule, FormsModule, HugeiconsIconComponent, ModalComponent],
    templateUrl: './case-document-center.component.html',
    styleUrl: './case-document-center.component.scss'
})
export class CaseDocumentCenterComponent implements OnInit, OnChanges {
    @Input() inquiryId: number | null = null;
    @Input() caseCompleted = false;

    groups: DocGroup[] = [];
    loading = false;
    baseUrl = '';

    // Upload modal
    showUploadModal = false;
    uploadFile: File | null = null;
    uploadDocType = '';
    uploading = false;

    // Icons
    fileIcon = File01Icon;
    uploadIcon = Upload01Icon;
    eyeIcon = EyeIcon;
    deleteIcon = Delete02Icon;

    private readonly allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'xlsx', 'xls', 'txt'];

    private readonly fileVariableLabels: Record<string, string> = {
        partial_closing_statement: 'Partial Closing Statement',
        bill_or_invoice: 'Itemized Bill / Invoice',
        hosipital_claim_lien: 'Hospital Claim of Lien',
        correspondence_lienholder: 'Correspondence with Hospital'
    };

    constructor(
        private caseService: CaseService,
        private snackbar: SnackbarService
    ) {}

    ngOnInit(): void {
        this.loadDocuments();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['inquiryId'] && !changes['inquiryId'].firstChange) {
            this.loadDocuments();
        }
    }

    loadDocuments(): void {
        if (!this.inquiryId) return;
        this.loading = true;
        this.caseService.getCaseDocuments(this.inquiryId).subscribe({
            next: (response) => {
                this.baseUrl = response?.default_url || '';
                this.groups = this.buildGroups(response);
                this.loading = false;
            },
            error: () => {
                this.groups = [];
                this.loading = false;
            }
        });
    }

    get hasDocuments(): boolean {
        return this.groups.some(g => g.docs.length > 0);
    }

    private buildGroups(data: any): DocGroup[] {
        const groups: DocGroup[] = [];

        // Patient Payment
        const patientPayment = data.patient_payment || data.payment;
        if (patientPayment) {
            const docs: DocItem[] = [];
            if (patientPayment.receipt_path) {
                docs.push({ name: patientPayment.receipt || 'Receipt', path: patientPayment.receipt_path, label: 'Payment Receipt' });
            }
            if (docs.length) groups.push({ title: 'Patient Payment', docs });
        }

        // Hospital Documents (hospital payment attachments + Release of Lien)
        const hospitalDocs: DocItem[] = [];
        if (data.hospital_payment_detail) {
            if (data.hospital_payment_detail.attachement_path) {
                hospitalDocs.push({ name: data.hospital_payment_detail.attachement || 'Attachment', path: data.hospital_payment_detail.attachement_path, label: 'Hospital Payment Attachment' });
            }
            if (data.hospital_payment_detail.admin_attachment_path) {
                hospitalDocs.push({ name: data.hospital_payment_detail.admin_attachment || 'Admin Attachment', path: data.hospital_payment_detail.admin_attachment_path, label: 'Admin Attachment' });
            }
        }
        const releaseOfLienDocs: any[] = (data.offer_documents || []).filter(
            (doc: any) => doc.name && (doc.type === 'Release of Lien' || doc.document_type === 'Release of Lien')
        );
        for (const doc of releaseOfLienDocs) {
            hospitalDocs.push({
                name: doc.name,
                path: doc.file_path ? `${doc.file_path}/${doc.name}` : '',
                label: 'Release of Lien'
            });
        }
        if (hospitalDocs.length) groups.push({ title: 'Hospital Documents', docs: hospitalDocs });

        // Easy Lien Payment
        if (data.easylien_payment_detail) {
            const docs: DocItem[] = [];
            if (data.easylien_payment_detail.attachement_path) {
                docs.push({ name: data.easylien_payment_detail.attachement || 'Attachment', path: data.easylien_payment_detail.attachement_path, label: 'Easy Lien Payment Attachment' });
            }
            if (docs.length) groups.push({ title: 'Easy Lien Payment', docs });
        }

        // Submission Documents
        if (data.documents?.length) {
            const docs: DocItem[] = data.documents.map((doc: any) => ({
                name: doc.name || 'Document',
                path: doc.storage_path || '',
                label: this.fileVariableLabels[doc.file_variable] || doc.file_variable || doc.name || 'Document'
            }));
            groups.push({ title: 'Submission Documents', docs });
        }

        // Offer Documents (excluding Release of Lien — that goes under Hospital Documents)
        if (data.offer_documents?.length) {
            const docs: DocItem[] = data.offer_documents
                .filter((doc: any) => doc.name && doc.type !== 'Release of Lien' && doc.document_type !== 'Release of Lien')
                .map((doc: any) => ({
                    name: doc.name,
                    path: doc.file_path ? `${doc.file_path}/${doc.name}` : '',
                    label: doc.type || 'Offer Document'
                }));
            if (docs.length) groups.push({ title: 'Offer Documents', docs });
        }

        // Additional Documents
        if (data.additional_docs?.length) {
            const docs: DocItem[] = data.additional_docs.map((doc: any) => ({
                name: doc.name || doc.filename || 'Document',
                path: doc.storage_path || doc.file_path || '',
                label: doc.document_type || doc.type || 'Additional Document'
            }));
            groups.push({ title: 'Additional Documents', docs });
        }

        return groups;
    }

    getFileExt(name: string): string {
        return name.split('.').pop()?.toUpperCase() || '';
    }

    getFileUrl(path: string): string {
        if (!path) return '';
        return this.baseUrl ? `${this.baseUrl}/${path}` : path;
    }

    viewDocument(path: string): void {
        const url = this.getFileUrl(path);
        if (url) window.open(url, '_blank');
    }

    openUploadModal(): void {
        this.uploadFile = null;
        this.uploadDocType = '';
        this.showUploadModal = true;
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            const file = input.files[0];
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            if (!this.allowedExtensions.includes(ext)) {
                this.snackbar.show(`Invalid file type. Allowed: ${this.allowedExtensions.join(', ')}`, 'error');
                input.value = '';
                return;
            }
            this.uploadFile = file;
        }
    }

    removeFile(): void {
        this.uploadFile = null;
    }

    submitUpload(): void {
        if (!this.inquiryId || !this.uploadFile) return;
        this.uploading = true;
        this.caseService.uploadCaseDocument(this.inquiryId, this.uploadFile).subscribe({
            next: () => {
                this.snackbar.show('Document uploaded successfully', 'success');
                this.showUploadModal = false;
                this.uploading = false;
                this.loadDocuments();
            },
            error: () => {
                this.snackbar.show('Failed to upload document', 'error');
                this.uploading = false;
            }
        });
    }
}
